<script>
import Layout from '@layouts/main'
import {
  cdmMethods,
  cdmrMethods,
  authComputed,
  dashboardComputed,
  dashboardMethods,
} from '@state/helpers'
import RevUsageTable from '@components/rev-usage-table'

export default {
  metaInfo: {
    title: 'View CDM',
    meta: [{ name: 'description', content: 'View CDM' }],
  },
  components: { Layout, RevUsageTable },
  props: {
    fields: {
      type: Array,
      required: true,
    },
    cdm: {
      type: Object,
      required: true,
    },
    approvalWorkflows: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      createError: null,
      isWorking: false,
      invalidCoding: {},
    }
  },
  computed: {
    ...authComputed,
    ...dashboardComputed,
    sortedFields() {
      var fields = this.fields
      return fields.sort(function(a, b) {
        return a.order - b.order
      })
    },
    itemNumber() {
      var field = null
      for (var i = 0; i < this.fields.length; i++) {
        if (this.fields[i].uniqueIdentifier) {
          field = this.fields[i]
          break
        }
      }

      if (!field) {
        return ''
      }

      return this.cdm[field.name]
    },
    userCanCreateCdmr() {
      for (var i = 0; i < this.approvalWorkflows.length; i++) {
        var aw = this.approvalWorkflows[i]

        for (var n = 0; n < aw.role.userRoles.length; n++) {
          var ur = aw.role.userRoles[n]
          if (ur.userId === this.currentUser.id) {
            return true
          }
        }
      }

      return false
    },
  },
  created() {
    this.loadCodingMetrics()
    if (Object.keys(this.invalidCoding).length === 0) {
      if (!this.currentUser.currentFacilityId) {
        return
      }

      this.codingMetrics({ facilityId: this.currentUser.currentFacilityId })
        .then(result => {
          this.loadCodingMetrics()
        })
        .catch(_ => {})
    }
  },
  methods: {
    ...cdmrMethods,
    ...cdmMethods,
    ...dashboardMethods,
    loadCodingMetrics() {
      if (
        !this.cachedFacilityCodingMetrics ||
        !this.cachedFacilityCodingMetrics[this.currentUser.currentFacilityId]
      ) {
        return {}
      }

      var cpt = this.cachedFacilityCodingMetrics[
        this.currentUser.currentFacilityId
      ].invalidCptCodes[this.itemNumber]

      var rev = this.cachedFacilityCodingMetrics[
        this.currentUser.currentFacilityId
      ].invalidRevCodes[this.itemNumber]

      var mod = this.cachedFacilityCodingMetrics[
        this.currentUser.currentFacilityId
      ].invalidModifiers[this.itemNumber]

      var merged = {}
      var i
      var key
      if (cpt) {
        for (i = 0; i < Object.keys(cpt).length; i++) {
          key = Object.keys(cpt)[i]
          merged[key] = cpt[key]
        }
      }

      if (rev) {
        for (i = 0; i < Object.keys(rev).length; i++) {
          key = Object.keys(rev)[i]
          merged[key] = rev[key]
        }
      }

      if (mod) {
        for (i = 0; i < Object.keys(mod).length; i++) {
          key = Object.keys(mod)[i]
          merged[key] = mod[key]
        }
      }

      this.invalidCoding = merged
    },
    fieldValidInvalidClass(field) {
      if (!this.invalidCoding) {
        return ''
      }

      var searchFor = field.name
      var found = false
      Object.keys(this.invalidCoding).forEach(function(key) {
        if (key === searchFor) {
          found = true
        }
      })
      return found ? 'invalidCoding' : ''
    },
    tryCreateCdmr() {
      this.isWorking = true
      this.createError = null

      return this.createCdmr({ cdmId: this.cdm.CMNIdField })
        .then(result => {
          this.isWorking = false

          this.$router.push({
            name: 'editCdmr',
            params: { cdmrId: result.id },
          })
        })
        .catch(error => {
          this.isWorking = false
          this.createError = error
          this.$swal(
            'Error',
            typeof error === 'string'
              ? error
              : 'An error occurred creating this CDM-R',
            'error'
          )
        })
    },
    tryCreatePurgeCdmr() {
      this.isWorking = true
      this.createError = null

      return this.createPurgeCdmr({ cdmId: this.cdm.CMNIdField })
        .then(result => {
          this.isWorking = false

          this.$router.push({
            name: 'editCdmr',
            params: { cdmrId: result.id },
          })
        })
        .catch(error => {
          this.isWorking = false
          this.createError = error
          this.$swal(
            'Error',
            typeof error === 'string'
              ? error
              : 'An error occurred creating this CDM-R',
            'error'
          )
        })
    },
  },
}
</script>

<template>
  <Layout>
    <v-container fluid grid-list-md ma-0>
      <v-layout>
        <!-- Data table -->
        <v-flex md-6>
          <v-card :class="$style.scrollbox">
            <table class="table">
              <tbody>
                <tr v-for="field in sortedFields" :key="field.id">
                  <td scope="row">
                    <label v-if="field.caption">{{ field.caption }}</label>
                    <label v-if="!field.caption">{{ field.name }}</label>
                  </td>
                  <th style="text-align: right;">
                    <label :class="fieldValidInvalidClass(field)">{{
                      cdm[field.name]
                    }}</label>
                  </th>
                </tr>
              </tbody>
            </table>
          </v-card>

          <!-- Table actions -->
          <v-layout justify-space-between mt-3 px-1>
            <BaseButton :disabled="isWorking" @click="tryCreateCdmr">
              <BaseIcon v-if="isWorking" name="sync" spin />
              <span v-else>Create CDM-R</span>
            </BaseButton>

            <BaseButton :disabled="isWorking" @click="tryCreatePurgeCdmr">
              <BaseIcon v-if="isWorking" name="sync" spin />
              <span v-else>Purge CDM</span>
            </BaseButton>
          </v-layout>
        </v-flex>

        <!-- Additional data -->
        <v-flex md-6>
          <v-card>
            <v-card-title>Additional Data</v-card-title>
            <v-card-media>
              <v-tabs slider-color="blue">
                <v-tab>Revenue Usage</v-tab>
                <v-tab-item>
                  <rev-usage-table :cdm-id="cdm.CMNIdField"></rev-usage-table>
                </v-tab-item>
              </v-tabs>
            </v-card-media>
          </v-card>
        </v-flex>
      </v-layout>
    </v-container>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.scrollbox {
  max-height: 70vh;
  overflow-y: auto;
}
</style>
